import { useEffect, useRef, useState } from 'react';

import CircularLoader from '@material-ui/core/CircularProgress';

import { fetchFaceAuthRecoveryStatus } from 'actions/other';
import { FACE_AUTH_STATUS } from 'constants/enums';
import i18n from 'i18n/config';
import { useAppDispatch } from 'storeHooks';
import { FetchFaceAuthRecoveryStatusSuccessResponse } from 'types/actions/other';
import Box from 'UIKit/Box';

import styles from './disable2faFaceAuth.module.scss';
import { PostSdkSuccessErrors } from './Disable2faFaceAuth.types';

const textSelfieVerificationLoading = i18n.t<string>(
  'auth:twoFaFaceAuth.selfieVerificationLoading'
);

const textMayTakeTime = i18n.t<string>('auth:twoFaFaceAuth.mayTakeSomeTime');

// please pass stable reference of onSuccess and onError
const PostSdkSuccess = ({
  onError,
  recoveryId,
  onSuccess,
}: {
  // please pass stable reference
  onSuccess?: () => void;
  // please pass stable reference
  onError: (reason: PostSdkSuccessErrors) => void;
  recoveryId: string;
}) => {
  const appDispatch = useAppDispatch();

  const timerCount = useRef<number>(0);

  const [successfulReset, setSuccessfulReset] = useState<boolean | null>(null);

  useEffect(() => {
    const intervalId = setInterval(() => {
      timerCount.current += 1;
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    // variable for ignoring previous api response in case of effect rerunning
    let cleanedUp: boolean = false;

    const fetchTwofaFaceAuthStatus = async () => {
      try {
        if (timerCount.current > 10) {
          onError('time_out');
          return;
        }
        const response: FetchFaceAuthRecoveryStatusSuccessResponse = await appDispatch(
          fetchFaceAuthRecoveryStatus({
            params: { id: recoveryId },
          })
        );

        if (cleanedUp) {
          return;
        }

        if (response?.result.status === FACE_AUTH_STATUS.TWO_FACTOR_RESET) {
          setSuccessfulReset(true);
          onSuccess?.();
        } else if (response?.result.status === FACE_AUTH_STATUS.FAILED) {
          onError(FACE_AUTH_STATUS.FAILED);
        } else {
          setTimeout(() => {
            fetchTwofaFaceAuthStatus();
          }, 2000);
        }
      } catch (error) {
        if (cleanedUp) {
          return;
        }

        setTimeout(() => {
          fetchTwofaFaceAuthStatus();
        }, 2000);
      }
    };

    fetchTwofaFaceAuthStatus();

    return () => {
      cleanedUp = true;
    };
  }, [appDispatch, onError, onSuccess, recoveryId]);

  if (successfulReset === null) {
    return (
      <Box className={styles.loadingContainer}>
        <Box className={styles.loaderContainer}>
          <CircularLoader
            classes={{ root: styles.rootLoader, colorPrimary: styles.loaderColor }}
            size={80}
          />
        </Box>
        <Box as="h2" fontSize="lg2" className={styles.loadingHeading}>
          {textSelfieVerificationLoading}
        </Box>
        <Box as="h4" className={styles.loadingDescription}>
          {textMayTakeTime}
        </Box>
      </Box>
    );
  }

  return null;
};

export default PostSdkSuccess;
